<template>
  <v-container class="no-margins">
    <GoBackAtTop
      v-if="!edit"
      :to="{name: 'EbookReportOverviewStart', meta: {preferLastPath: true}}"
      :label="$t('labels.reports')"
    />

    <div class="title-icon">
      <h1 class v-if="edit">{{ $t('ebook.summary') }}</h1>
      <h1 class v-else>{{ $t('ebook.report-show') }}</h1>
      <v-btn icon href="https://www.byggbranschensyrkesnamnd.se/manual/sammanfattning/" target="_blank">
        <v-icon>$sl-question</v-icon>
      </v-btn>
    </div>
    <h4>{{ $t('labels.time-location')}}</h4>

    <div class="title-icon">
      <div class="overline">{{ $t('labels.instructor')}}</div>
      <v-icon
        v-if="edit"
        @click="$emit('goto', {report: currentReport, goto: 'instructor'})"
      >$sl-navigate-left</v-icon>
    </div>
    <p>{{ instructorName }}</p>

    <div class="title-icon">
      <div class="overline">{{ $t('ebook.period')}}</div>
      <v-icon
        v-if="edit"
        @click="$emit('goto', {report: currentReport, goto: 'period'})"
      >$sl-navigate-left</v-icon>
    </div>
    <div class="d-flex" v-if="currentReport.fromDate==currentReport.toDate">
      <div>{{ $t('labels.date')}}:</div>
      <div class="pl-12">{{ dateFormat(currentReport.fromDate) }}</div>
    </div>
    <div v-else>
      <p class="mb-1">{{ $t('labels.from')}}: {{ dateFormat(currentReport.fromDate) }}</p>

      <p>{{ $t('labels.to')}}: {{ dateFormat(currentReport.toDate) }}</p>
    </div>
    <div class="title-icon mt-6">
      <div class="overline">{{ $t('labels.workplace')}}</div>
      <v-icon
        v-if="edit"
        @click="$emit('goto', {report: currentReport, goto: 'workplace'})"
      >$sl-navigate-left</v-icon>
    </div>
    <p>{{ workplace }}</p>

    <h4 class="mt-8">{{ $t('ebook.elementpart')}}</h4>

    <v-list one-line class="skills mt-4">
      <v-card class="mb-4" flat v-for="(item,index) in repDetails" :key="index">
        <v-list-item :class="`skill${item.apprenticeshipSkillEstimatedLevel}`">
          <v-icon
            class="skill-level"
            v-if="item.apprenticeshipSkillEstimatedLevel==1"
          >$sl-ebook-lvl1</v-icon>
          <v-icon
            class="skill-level"
            v-else-if="item.apprenticeshipSkillEstimatedLevel==2"
          >$sl-ebook-lvl2</v-icon>
          <v-icon
            class="skill-level"
            v-else-if="item.apprenticeshipSkillEstimatedLevel==3"
          >$sl-ebook-lvl3</v-icon>
          <v-icon
            class="skill-level"
            v-else-if="item.apprenticeshipSkillEstimatedLevel==4"
          >$sl-ebook-lvl4</v-icon>
          <v-icon class="skill-level" v-else></v-icon>

          <v-list-item-content class="ml-2 d-flex align-center">
            <div class="overline regular">
              {{item.subElement.element.name}} > {{item.subElement.name}}
            </div>

            <div class="medium" v-if="get(item, 'apprenticeComment', null)">{{ get(item, 'apprenticeComment', '') | comment}}</div>
          </v-list-item-content>
          
          <v-btn
            v-if="edit"
            class="elevation font-weight-bold"
            color="primary"
            :style="{ height: '40px' }"
            @click="$emit('goto', {report: currentReport, goto: 'element', item: item})"
          >
            {{ $t('ebook.rate-report') }}
          </v-btn>

          <v-icon v-else @click="openElementModal(item)">$sl-view</v-icon>
        </v-list-item>
      </v-card>
    </v-list>
    <v-dialog persistent v-model="openModal" max-width="700px" class="pa-0">
      <EbookElementModalJudgement
        :currentReport="currentReport"
        :repDetail="selectedElement"
        :elements="repDetails"
        :isEditable="false"
        :show="'elementJudgement'"
        @close="closeModal"
      ></EbookElementModalJudgement>
    </v-dialog>

    <template v-if="edit">
      <h4 class="pt-4">{{ $t('ebook.text-area-review-title')}}</h4>

      <v-textarea
        :disabled="!edit"
        auto-grow
        outlined
        v-model="comment"
        type="text"
        class="mt-4"
        row-height="25"
        rows="3"
        textarea
      ></v-textarea>
    </template>
    <template v-else>
      <div v-if="comment && comment!=''">
        <h4 class="pt-4">{{ $t('ebook.my-comment')}}</h4>
        <p>{{ comment }}</p>
      </div>
      <div v-if="instructorComment && instructorComment!=''">
        <h4 class="pt-4">{{ instructorFirstName | filterGenitiveName }} {{ $t('ebook.comment')}}</h4>
        <p>{{ instructorComment }}</p>
      </div>
    </template>

    <!-- <v-divider class="mb-2" v-if="instructorComment!='' || comment!=''"></v-divider> -->

    <ActionButton
      v-if="edit"
      class="mt-2"
      :type="'application'"
      smallerText
      @click="saveClick(false)"
      :disabled="valid || isImitatingUser()"
      actionIcon="$sl-notes"
    >{{ $t('labels.save-draft') }}</ActionButton>
    <ActionButton
      v-if="edit"
      class="mt-6"
      smallerText
      @click="saveClick(true)"
      :disabled="valid || isImitatingUser()"
      actionIcon="$sl-save"
    >{{ $t('labels.save-ebook') }}</ActionButton>

    <!-- <ActionButton
      v-if="edit"
      actionIcon="$sl-delete"
      class="mt-6"
      @click="deleteClick"
      type="application"
    >{{ $t('ebook.delete-report')}}</ActionButton>-->

    <BottomNav v-if="edit" @clicked="$emit('goto', {report: currentReport, goto: 'element'})" />
    <ModalBase
        :show="showModal"
        maxWidth="400px"
        :title="$t('ebook.save-report-title')"
        cancelIcon="$sl-cancel"
        submitIcon="$sl-save"
        :cancelText="$t('labels.cancel')"
        :submitText="$t('labels.save')"
        @close="showModal=false"
        @cancel="showModal=false"
        @submit="updateReport(true)"
        :outlinedSubmit="true">
          <template v-slot:text>
            {{ $t('ebook.save-report-message') }}
          </template>
    </ModalBase>

  </v-container>
  <!-- <v-dialog persistent v-model="showDialog" max-width="700px">
      <EbookProfessionInfoModal :show="openModal" @close="close" :element="this.dialogElement"></EbookProfessionInfoModal>
  </v-dialog>-->
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import API from '@/services/api.js'
import GoBackAtTop from '@/components/Common/GoBackAtTop'
import EbookElementModalJudgement from '@/components/Ebook/Apprentice/EbookElementModalJudgement'
import ActionButton from '@/components/Common/ActionButton'
import BottomNav from '@/components/Application/BottomNav'
import ModalBase from '@/components/Common/ModalBase';
import ModalBaseMixin from "@/components/mixins/ModalBaseMixin";
import get from 'lodash/get'

// @group Ebook
// Styled EbookReview component
export default {
  name: 'EbookReview',

  mixins: [ModalBaseMixin],

  props: {
    //has current report
    currentReport: Object
  },

  components: {
    BottomNav,
    ActionButton,
    EbookElementModalJudgement,
    GoBackAtTop,
    ModalBase
  },

  data: () => ({
    valid: false,
    openModal: false,
    comment: '',
    instructorComment: '',
    workplace: '',
    instructorName: '',
    instructorFirstName: '',
    commentLabel: '',
    selectedElement: {},
    edit: false,
    repDetails: []
  }),

  created() {
    this.init()
  },

  watch: {
    currentReport: {
      handler: function() {
        this.init()
      },
      deep: true
    }

    // currentReport(val) {
    //   this.init()
    // }
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isApprentice', 'isInstructor', 'isImitatingUser'])
  },

  filters: {
    comment(val) {
      if (val && val.length > 44) {
        return val.substring(0, 44) + '...'
      } else return val
    },
    filterGenitiveName(name) {
      if (name && name.length > 0) {
        if (name.slice(-1) == 's') {
          return name
        }
        return name + 's'
      } else {
        return name + 'BYNS'
      }
    }
  },
  methods: {
    get,

    init() {
      this.getReportDetailsList()

      if (
        this.currentReport &&
        this.currentReport.instructor &&
        this.currentReport.instructor.employed &&
        this.currentReport.instructor.employed.firstName &&
        this.currentReport.instructor.employed.lastName
      ){
        this.instructorName = this.currentReport.instructor.employed.firstName + ' ' + this.currentReport.instructor.employed.lastName
        this.instructorFirstName = this.currentReport.instructor.employed.firstName
      }
        
      if (this.currentReport && this.currentReport.apprenticeComment) {
        this.comment = this.currentReport.apprenticeComment
      } else {
        this.commentLabel = this.$t('ebook.judge-comment')
      }
      if (this.currentReport && this.currentReport.instructorComment) {
        this.instructorComment = this.currentReport.instructorComment
      }

      if (this.currentReport && this.currentReport.workplace && this.currentReport.workplace.name)
        this.workplace = this.currentReport.workplace.name

      if (this.currentReport && this.currentReport.workplace && this.currentReport.workplace.postalCity)
        this.workplace += ' ' + this.currentReport.workplace.postalCity

      if (!this.currentReport.status || this.currentReport.status == '' || this.currentReport.status == 'draft') {
        this.edit = true
      }
    },
    openElementModal(element) {
      this.selectedElement = element
      this.openModal = true
    },
    closeModal() {
      this.openModal = false
    },
    dateFormat(date) {
      //if (date && date != '') return SugarDate.Date(date).format('%Y-%m-%d')
      if (date && date.length > 10) return date.substring(0, 10)
      else {
        return ''
      }
    },
    getReportDetailsList() {
      API.listReportDetails(this.currentReport.bookId, this.currentReport.id)
        .then(response => {
          this.repDetails = response.data.reportDetails
        })
        .catch(error => {
          console.log('Error ' + error)
        })
    },
    /**
     * @vuese
     * Used to update report
     * @arg The arguments are an integers representing current book id and current report id and as params is current report object
     */
    async saveClick(status) {
      if(!this.currentReport.instructorId) {
        this.$events.fire('showError', this.$t('errors.instructor'))
        return
      }
      if (this.comment) {
        this.currentReport.apprenticeComment = this.comment
      }
      if (
        this.currentReport &&
        this.currentReport.id > 0 &&
        this.currentReport.bookId > 0
      ) {
        if(status) {
          this.showModal=true
        }
        else {
          this.updateReport(status)
        }
      }
      //     const res = await this.$dialog.confirm({
      //       title: this.$t('ebook.save-report-title'),
      //       text: this.$t('ebook.save-report-message'),
      //       actions: [
      //         {text: this.$t('labels.cancel'), key: false, icon: '$sl-cancel'},
      //         {text: this.$t('labels.save'), key: true, icon: '$sl-save', outlined: true}
      //       ],
      //     });
      //     if (res !== true) return;
      //   }
      //   }
      },
      updateReport(status){
      if (this.comment) {
        this.currentReport.apprenticeComment = this.comment
      }
        this.currentReport.changeStatus = status
        API.updateReport(this.currentReport.bookId, this.currentReport.id, {
          data: this.currentReport
        })
          .then(response => {
            this.$events.fire('showInfo', this.$t('success.saved'))
            if(status){
              setTimeout(() => {
                this.$router.push({name: 'EbookReportOverviewStart', params: {id: this.$route.params.id}})
              }, 1000)
            }
            else {
              setTimeout(() => {
                this.$router.push({name: 'EbookReportElement', params: {id: this.$route.params.id}})
              }, 1000)            
            }
          })
          .catch(error => {
            console.log('Error: ' + error)
          })
      
    },
    /**
     * @vuese
     * Used to delete report
     * @arg The arguments are an integers representing current book id and current report id
     */
    async deleteClick() {
      const res = await this.$dialog.confirm({
        title: 'Radera rapporten?',
        text: 'Vill du radera denna rapport?',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel', outlined: true},
          {text: 'JA', key: true, icon: '$sl-save'}
        ]
      })

      if (res == true) {
        API.deleteReport(this.currentReport.bookId, this.currentReport.id)
          .then(response => {
            this.$events.fire('showInfo', this.$t('success.deleted'))

            return new Promise(resolve => {
              setTimeout(() => {
                this.$router.push({name: 'EbookReportElement', params: {id: this.$route.params.id}})
              }, 1000)
            })
          })
          .catch(error => {
            this.$events.fire('showError', this.$t('errors.delete-failure'))
            console.log('There was an error:', error)
          })
      } else {
        return
      }
    }
  }
}
</script>

<style lang="scss">
</style>
